import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  Icon,
} from "@mui/material";
import { FormData, JournalTotals, FormValidationState } from "../utils";

interface PostConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  formData: FormData;
  totals: JournalTotals;
  validationState: FormValidationState;
  isLoading: boolean;
}

export const PostConfirmationDialog: React.FC<PostConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  formData,
  totals,
  validationState,
  isLoading,
}) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: "warning.main" }}>warning</Icon>
          Confirm Post Entry
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to post this journal entry? Once posted, the
          entry cannot be modified.
        </Typography>
        <Box
          sx={{
            bgcolor: alpha(theme.palette.info.main, 0.1),
            p: 2,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Entry Summary:
          </Typography>
          <Typography variant="body2">
            • Total Debit: {formData.currency} {totals.totalDebit.toFixed(2)}
          </Typography>
          <Typography variant="body2">
            • Total Credit: {formData.currency} {totals.totalCredit.toFixed(2)}
          </Typography>
          <Typography variant="body2">
            • Lines: {formData.journal_lines.length}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: validationState.isBalanced ? "success.main" : "error.main",
              fontWeight: 600,
              mt: 1,
            }}
          >
            • Status:{" "}
            {validationState.isBalanced ? "✓ Balanced" : "⚠ Not Balanced"}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isLoading || !validationState.canPost}
          startIcon={<Icon>check_circle</Icon>}
          color="primary"
          title={
            !validationState.canPost
              ? "Entry must be balanced and complete to post"
              : "Confirm posting to ledger"
          }
        >
          {isLoading ? "Posting..." : "Confirm Post"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
